import {
  formatNumber,
  formatDuration,
  formatWeight,
  formatPace,
  formatPercent,
  deltaSign
} from "../utils/format.js";
import styles from "./KPIGrid.module.css";

function DeltaBadge({ delta, prev }) {
  if (!delta || delta.direction === "flat" || delta.previous === 0) {
    return <span className={`${styles.delta} ${styles.flat}`}>→ stable</span>;
  }
  const sign = deltaSign(delta.direction);
  const pct = delta.percent != null ? `${Math.abs(delta.percent * 100).toFixed(0)}%` : null;
  return (
    <span className={`${styles.delta} ${styles[delta.direction]}`}>
      {sign} {pct ?? "—"}
      {prev && <span className={styles.prev}> · last period {prev}</span>}
    </span>
  );
}

function KPICard({ label, value, sub, delta, prev, index }) {
  return (
    <div className={`${styles.card} kpi-card`} style={{ '--i': index }}>
      <span className={styles.label}>{label}</span>
      <strong className={styles.value}>{value}</strong>
      {sub && <small className={styles.sub}>{sub}</small>}
      <DeltaBadge delta={delta} prev={prev} />
    </div>
  );
}

export default function KPIGrid({ analytics, weightUnit, periodDays }) {
  if (!analytics) {
    return (
      <div className={styles.grid}>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className={`${styles.card} ${styles.skeleton} kpi-card`} aria-hidden="true" />
        ))}
      </div>
    );
  }

  const { current, deltas } = analytics;
  const avgDuration = formatDuration(current.averageDurationSeconds);

  const p = (delta, fmt) => (delta?.previous > 0 ? fmt(delta.previous) : null);

  const cards = [
    {
      label: "Workouts",
      value: formatNumber(current.workoutCount),
      sub: `${formatPace(current.weeklyWorkoutAverage)} / week avg`,
      delta: deltas?.workoutCount,
      prev: p(deltas?.workoutCount, formatNumber)
    },
    {
      label: "Training time",
      value: formatDuration(current.totalDurationSeconds),
      sub: `${avgDuration} avg`,
      delta: deltas?.totalDurationSeconds,
      prev: p(deltas?.totalDurationSeconds, formatDuration)
    },
    {
      label: "Volume",
      value: formatWeight(current.totalVolumeKg, weightUnit),
      sub: `${formatWeight(current.averageVolumeKg, weightUnit)} / workout`,
      delta: deltas?.totalVolumeKg,
      prev: p(deltas?.totalVolumeKg, (v) => formatWeight(v, weightUnit))
    },
    {
      label: "Sets",
      value: formatNumber(current.totalSets),
      sub: `${formatNumber(Math.round(current.averageSets))} avg / workout`,
      delta: deltas?.totalSets,
      prev: p(deltas?.totalSets, formatNumber)
    },
    {
      label: "PR sets",
      value: formatNumber(current.prSetCount),
      sub: `${formatPercent(current.prRate)} of sets`,
      delta: deltas?.prSetCount,
      prev: p(deltas?.prSetCount, formatNumber)
    }
  ];

  const kcal = (v) => `${formatNumber(Math.round(v))} kcal`;
  const bpm = (v) => `${Math.round(v)} bpm`;

  if (current.caloriesWorkoutCount > 0) {
    cards.push({
      label: "Calories",
      value: kcal(current.totalCalories),
      sub: `${kcal(current.averageCalories)} avg · ${current.caloriesWorkoutCount} tracked`,
      delta: deltas?.totalCalories,
      prev: p(deltas?.totalCalories, kcal)
    });
  }
  if (current.heartRateWorkoutCount > 0) {
    cards.push({
      label: "Heart rate",
      value: bpm(current.averageHeartRate),
      sub: current.maxHeartRateWorkoutCount > 0
        ? `${bpm(current.maxHeartRate)} peak · ${current.heartRateWorkoutCount} tracked`
        : `${current.heartRateWorkoutCount} tracked`,
      delta: deltas?.averageHeartRate,
      prev: p(deltas?.averageHeartRate, bpm)
    });
  }

  return (
    <div className={styles.grid}>
      {cards.map((card, i) => (
        <KPICard key={`${card.label}-${periodDays}`} {...card} index={i} />
      ))}
    </div>
  );
}
